/**
 * API client.
 *
 * One place that knows how to talk to the backend. In Phase 1 the dev header is
 * replaced by a Cognito bearer token and nothing else here changes — which is why
 * every screen goes through this module rather than calling fetch directly.
 */

/*
 * Where the backend is.
 *
 * `||` rather than `??` on purpose: an unset variable and one set to the empty string
 * are the same mistake, and `??` would only catch the first. A build that shipped an
 * empty string here produced a portal that fetched relative URLs, got index.html back,
 * and failed with a JSON parse error pointing at nothing.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

if (import.meta.env.PROD && BASE_URL.startsWith('http://localhost')) {
  // A production bundle pointing at localhost cannot work: the page is served over
  // HTTPS, so the browser blocks the call as mixed content and reports only
  // "Failed to fetch" — no status, no log, nothing to search for. Say it once, loudly,
  // where whoever opens the console will see it.
  console.error(
    '[hh] VITE_API_BASE_URL was not set at build time; every request will fail. ' +
      'Set the TEST_API_URL / PROD_API_URL repository variable and rebuild.',
  );
}

/**
 * How a request proves who is calling.
 *
 * Either a Cognito bearer token or, locally, the dev-user header the backend accepts
 * only when ALLOW_DEV_AUTH is set. Supplied by AuthProvider at startup rather than
 * imported, so this module stays free of the auth SDK — it is used by tests and by the
 * offline scan page, neither of which should pull in Amplify.
 */
export type AuthCredential = { token: string } | { devUser: string } | null;
type TokenProvider = () => Promise<AuthCredential>;

const FALLBACK_DEV_USER = import.meta.env.VITE_DEV_USER as string | undefined;

let tokenProvider: TokenProvider = async () =>
  FALLBACK_DEV_USER ? { devUser: FALLBACK_DEV_USER } : null;

export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

/**
 * Called when the API rejects a token, so the app can send the user back to sign in
 * rather than showing "Autenticazione richiesta" on every panel of the dashboard.
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const credential = await tokenProvider();
  if (!credential) return {};
  return 'token' in credential
    ? { authorization: `Bearer ${credential.token}` }
    : { 'x-dev-user': credential.devUser };
}

// --- Waking up -----------------------------------------------------------------
//
// The database pauses when nobody has used it for a few minutes (that is what keeps it
// free overnight) and needs ~15 s to come back. The first requests after a pause wait
// for it. Before this, they simply failed with "Failed to fetch" and a refresh often
// failed again, which read as a broken portal. Now reads — and anything the server says
// was refused because the database was still starting, which guarantees nothing ran —
// are retried quietly for up to WAKE_BUDGET_MS, and the layout shows a small banner.

const WAKE_BUDGET_MS = 40_000;
const wakeListeners = new Set<(waking: boolean) => void>();
let wakingCount = 0;

function setWaking(delta: 1 | -1) {
  const before = wakingCount > 0;
  wakingCount = Math.max(0, wakingCount + delta);
  const after = wakingCount > 0;
  if (before !== after) wakeListeners.forEach((fn) => fn(after));
}

/** For the "starting up" banner: called with true while any request is waiting. */
export function subscribeWaking(fn: (waking: boolean) => void): () => void {
  wakeListeners.add(fn);
  return () => wakeListeners.delete(fn);
}
export const isWaking = () => wakingCount > 0;

/**
 * Nudge the database awake. Fired once when the portal opens — on the sign-in screen,
 * before a password is typed — so the wake-up overlaps with signing in.
 */
/**
 * "Forgot password" for an account that never had one: asks the API to send the
 * invitation again. No token — the person is signed out by definition. Answers the same
 * whatever happened, so it tells nobody whether an address has an account.
 */
export async function requestInvitation(email: string): Promise<void> {
  await fetch(`${BASE_URL}/auth/invitation`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

export function wakeBackend(): void {
  fetch(`${BASE_URL}/health/wake`).catch(() => undefined);
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const safeToRepeat = method === 'GET' || method === 'HEAD';
  const started = Date.now();
  let attempt = 0;
  let waiting = false;

  try {
    for (;;) {
      let response: Response;
      try {
        response = await fetch(`${BASE_URL}${path}`, {
          ...init,
          headers: {
            'content-type': 'application/json',
            ...(await authHeaders()),
            ...(init.headers ?? {}),
          },
        });
      } catch (error) {
        // No response at all: offline, or a gateway timeout without CORS headers. Only a
        // read is repeated — a write might have landed before the connection dropped.
        if (safeToRepeat && Date.now() - started < WAKE_BUDGET_MS) {
          if (!waiting) {
            waiting = true;
            setWaking(1);
          }
          await sleep(Math.min(1500 * 2 ** attempt++, 6000));
          continue;
        }
        throw new ApiError(0, 'NETWORK', error instanceof Error ? error.message : 'Network error');
      }

      const starting = response.status === 503 || response.status === 504;
      if (starting && Date.now() - started < WAKE_BUDGET_MS) {
        // DATABASE_STARTING is the server promising nothing ran, so even a write is safe
        // to send again. A bare 503/504 from the gateway carries no such promise.
        let code = '';
        try {
          code = String(((await response.clone().json()) as { error?: string }).error ?? '');
        } catch {
          /* not JSON: a gateway error */
        }
        if (code === 'DATABASE_STARTING' || safeToRepeat) {
          if (!waiting) {
            waiting = true;
            setWaking(1);
          }
          await sleep(Math.min(1500 * 2 ** attempt++, 6000));
          continue;
        }
      }

      return await finish<T>(response);
    }
  } finally {
    if (waiting) setWaking(-1);
  }
}

async function finish<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let code = 'UNKNOWN';
    let message = `Errore ${response.status}`;
    let details: Record<string, unknown> | undefined;

    try {
      const body = (await response.json()) as Record<string, unknown>;
      code = String(body.error ?? code);
      message = String(body.message ?? message);
      details = body;
    } catch {
      // A non-JSON error body is still an error; don't lose the status behind a parse.
    }

    /*
     * An expired or revoked token is not a page-level error; it is the end of the
     * session. Without this every screen renders its own "authentication required"
     * box and the only way out is a manual reload.
     */
    if (response.status === 401) onUnauthorized?.();

    throw new ApiError(response.status, code, message, details);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const get = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const put = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });

// --- Types (generated from the OpenAPI spec in Phase 1) ----------------------

/**
 * The portal's sections, as the API names them. Mirrors SECTIONS in the backend; a
 * section the server does not know is rejected there, so this list only decides what
 * the grid renders.
 */
export const SECTIONS = [
  'dashboard',
  'shifts',
  'payroll',
  'assignments',
  'jobs',
  'employees',
  'sites',
  'documents',
  'compliance',
  'alerts',
  'inventory',
  'vehicles',
  'audit',
  'recycle_bin',
  'settings',
  'users',
  'messages',
  'calendar',
  'leave',
] as const;
export type Section = (typeof SECTIONS)[number];
export type PermissionLevel = 'none' | 'read' | 'write';
export type PermissionGrid = Record<Section, PermissionLevel>;

export interface ContactMessage {
  id: string;
  name: string;
  company: string | null;
  email: string;
  phone: string | null;
  subject: string | null;
  message: string;
  locale: string;
  status: 'new' | 'read' | 'archived';
  createdAt: string;
  readAt: string | null;
  readByName: string | null;
  archivedAt: string | null;
}

export type NotificationChannel = 'in_app' | 'email' | 'push';
export type NotificationGroup = 'schedule' | 'documents' | 'equipment' | 'office';

/** One notification, already written in the language asked for. */
export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  link: string | null;
  createdAt: string;
  readAt: string | null;
}

export interface NotificationTypePreference {
  key: string;
  group: NotificationGroup;
  audience: 'personal' | 'office';
  timing: { unit: 'minutes' | 'days'; options: number[]; defaults: number[]; multiple: boolean } | null;
  /** What this type can be sent by at all. */
  channels: NotificationChannel[];
  enabled: boolean;
  selectedChannels: NotificationChannel[];
  leadTimes: number[];
}

export interface NotificationSettings {
  quietEnabled: boolean;
  quietFrom: string;
  quietTo: string;
  locale: 'it' | 'en' | 'ar';
}

export interface NotificationPreferences {
  types: NotificationTypePreference[];
  settings: NotificationSettings;
  /** Whether each channel can actually reach this person right now. */
  available: { email: boolean; push: boolean; webPush: boolean };
}

export interface PermissionsResponse {
  role: 'admin' | 'manager' | 'engineer';
  sections: readonly Section[];
  /** Effective level per section: explicit grant, or the role's default. */
  permissions: PermissionGrid;
  /** Only the sections set explicitly. Absent on /me. */
  explicit?: Partial<PermissionGrid>;
}

export interface User {
  id: string;
  /** Null for someone on the books without an account yet — added before their address was known. */
  email: string | null;
  /**
   * What they type in the app instead of an email (mario.rossi), when they sign in by
   * name. Null for everyone who signs in with an email.
   */
  loginName: string | null;
  phone: string | null;
  firstName: string;
  lastName: string;
  role: 'admin' | 'manager' | 'engineer';
  employmentType: string;
  status: 'invited' | 'active' | 'suspended' | 'archived';
  /**
   * From Cognito, not the database: invited = never signed in, none = should be able to
   * sign in but has no account, null = unknown (local development).
   */
  signIn: 'invited' | 'active' | 'none' | null;
  /** Which doors: the internal portal, the mobile app. One sign-in, two flags. */
  portalAccess: boolean;
  appAccess: boolean;
  locationNoticeAckAt: string | null;
  createdAt: string;
  /** Personal details. The person keeps phone, address and emergency contact; the
   *  office keeps the rest. */
  jobTitle?: string | null;
  dateOfBirth?: string | null;
  /** Where they were born: on the identification card. */
  birthPlace?: string | null;
  taxCode?: string | null;
  hiredOn?: string | null;
  badgeNumber?: string | null;
  address?: string | null;
  emergencyContact?: string | null;
  /** Short-lived signed link to the photo on the ID badge. */
  photoUrl?: string | null;
}

export type PersonalDetails = Partial<
  Pick<
    User,
    | 'firstName'
    | 'lastName'
    | 'phone'
    | 'jobTitle'
    | 'dateOfBirth'
    | 'birthPlace'
    | 'taxCode'
    | 'hiredOn'
    | 'badgeNumber'
    | 'address'
    | 'emergencyContact'
  >
>;

export interface Shift {
  id: string;
  userId: string;
  jobId: string | null;
  siteId: string | null;
  status: 'open' | 'closed' | 'auto_closed' | 'disputed';
  clockInAt: string;
  clockOutAt: string | null;
  breakMinutes: number;
  durationMinutes: number | null;
  clockInWithinGeofence: boolean | null;
  clockInDistanceM: number | null;
  clockOutWithinGeofence: boolean | null;
  clockOutDistanceM: number | null;
  exceptionFlags: string[];
  overrideReason: string | null;
  clientEventId: string | null;
  /** When the office signed this off. Null while it is still in the review queue. */
  reviewedAt: string | null;
  reviewedByName: string | null;
  reviewNote: string | null;
  /** Which job and where. Null on a shift with no job, or no site. */
  jobReference?: string | null;
  jobTitle?: string | null;
  siteName?: string | null;
  siteAddress?: string | null;
  /** The positions the phone reported, with how sure it was (metres). */
  clockInLat?: number | null;
  clockInLng?: number | null;
  clockInAccuracyM?: number | null;
  clockOutLat?: number | null;
  clockOutLng?: number | null;
  clockOutAccuracyM?: number | null;
}

export interface ShiftAdjustment {
  id: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  reason: string;
  adjusted_at: string;
  adjusted_by_name: string;
}

export interface Job {
  id: string;
  reference: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  requiresSignature: boolean;
  /** Working-hours policy. Null means the job follows the global setting. */
  allowClockInBeforeStart: boolean | null;
  allowClockOutAfterEnd: boolean | null;
  client: { id: string; name: string | null } | null;
  site: {
    id: string;
    name: string | null;
    addressLine1: string | null;
    city: string | null;
    postcode: string | null;
    accessNotes: string | null;
    geofenceRadiusM: number | null;
    lat: number | null;
    lng: number | null;
  } | null;
  assignedUserIds: string[];
  teamNames: string[];
  documentIds: string[];
}

export interface LatLng {
  lat: number;
  lng: number;
}

export interface Site {
  id: string;
  name: string;
  client: { id: string; name: string | null } | null;
  addressLine1: string | null;
  city: string | null;
  postcode: string | null;
  geofenceRadiusM: number;
  accessNotes: string | null;
  isActive: boolean;
  lat: number | null;
  lng: number | null;
  /** A drawn boundary. When present it replaces the radius for clock-in verdicts. */
  geofencePolygon: LatLng[] | null;
  /** Grace outside the fence, in metres. Zero for an ordinary site. */
  edgeToleranceM: number;
  /** Per-site overrides. Null means the site follows the global setting. */
  accuracyTrustThresholdM: number | null;
  accuracyRejectThresholdM: number | null;
  allowOutside: boolean | null;
}

/** One row of the address dropdown, already carrying its coordinates. */
export interface AddressSuggestion {
  label: string;
  addressLine1: string | null;
  city: string | null;
  province: string | null;
  postcode: string | null;
  country: string;
  lat: number | null;
  lng: number | null;
}

/** Mirrors the doc_category enum; the server rejects anything else. */
export type DocumentCategory =
  | 'contract'
  | 'drawing'
  | 'certificate'
  | 'risk_assessment'
  | 'datasheet'
  | 'site_photo'
  | 'signed_sheet'
  | 'signature'
  | 'id_document'
  | 'other';

export type DocumentVisibility = 'office_only' | 'assigned_engineers' | 'all_staff';

/** What the server will accept. Checked here too, so a 40 MB mistake fails instantly
 *  rather than after the upload. Keep in step with hh-backend/src/lib/s3.ts. */
export const ALLOWED_UPLOAD_MIME = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/pjpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'image/tiff',
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/pkcs7-mime',
  'application/x-pkcs7-mime',
  'application/pkcs7-signature',
];

/**
 * Extensions, for the picker's filter and for files the browser gives no type.
 *
 * Windows has no type for .p7m (a digitally signed PDF — how nominations usually
 * arrive), HEIC or often .odt, so a type-only filter hid them from the file picker and
 * a type-only check refused them. The server makes the same extension fallback.
 */
export const ALLOWED_UPLOAD_EXTENSIONS = [
  '.pdf', '.p7m', '.jpg', '.jpeg', '.jfif', '.png', '.heic', '.heif', '.webp', '.tif', '.tiff',
  '.doc', '.docx', '.xls', '.xlsx', '.odt', '.ods',
];

/** Whether a picked file is one the server will take — by type, or by extension. */
export function isAcceptedUpload(file: { name: string; type: string }): boolean {
  if (file.type && ALLOWED_UPLOAD_MIME.includes(file.type.toLowerCase())) return true;
  const dot = file.name.lastIndexOf('.');
  const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
  const undeclared = !file.type || file.type === 'application/octet-stream';
  return undeclared && ALLOWED_UPLOAD_EXTENSIONS.includes(ext);
}

/** Image size cap applies to images, whether the type or the extension says so. */
export function isImageUpload(file: { name: string; type: string }): boolean {
  return file.type.startsWith('image/') || /\.(jpe?g|jfif|png|heic|heif|webp|tiff?)$/i.test(file.name);
}
export const MAX_IMAGE_BYTES = 25 * 1024 * 1024;
export const MAX_FILE_BYTES = 50 * 1024 * 1024;

export type RecycleKind = 'site' | 'job' | 'client' | 'document' | 'inventory';

export type InventoryStatus = 'available' | 'assigned' | 'maintenance' | 'lost' | 'retired';

export interface InventoryHolder {
  kind: 'user' | 'site';
  id: string;
  name: string | null;
}

/**
 * A category.
 *
 * `slug` is set only on the categories shipped with the system; those are ours to
 * translate. A category the office typed has a null slug and is shown exactly as typed
 * — turning somebody's own word into a different one because the language toggle moved
 * would be the worse bug.
 */
export interface InventoryCategory {
  id: string;
  name: string | null;
  slug: string | null;
}

export interface InventoryItem {
  id: string;
  /** On the label, and what a scan resolves to. Generated server-side. */
  code: string;
  name: string;
  description: string | null;
  category: InventoryCategory | null;
  serialNumber: string | null;
  manufacturer: string | null;
  model: string | null;
  purchasedOn: string | null;
  purchasePrice: number | null;
  status: InventoryStatus;
  codeKind: 'qr' | 'barcode';
  externalCode: string | null;
  photoDocumentId: string | null;
  /**
   * A short-lived URL the browser can put in an `<img>`, or null when there is no photo.
   * Presigned per response, so it is not worth storing anywhere.
   */
  photoUrl: string | null;
  notes: string | null;
  holder: InventoryHolder | null;
  checkedOutAt: string | null;
  dueBackOn: string | null;
  /** Days past the return date, or null when not out / not late. */
  overdueDays: number | null;
}

export interface InventoryMovement {
  id: string;
  checked_out_at: string;
  returned_at: string | null;
  due_back_on: string | null;
  checkout_notes: string | null;
  return_notes: string | null;
  user_name: string | null;
  site_name: string | null;
  job_reference: string | null;
  checked_out_by_name: string | null;
  returned_to_name: string | null;
}

export interface RecycleItem {
  kind: RecycleKind;
  id: string;
  label: string | null;
  detail: string | null;
  deletedAt: string;
  deletedByName: string | null;
  /** Days before the nightly purge takes it for good. */
  daysLeft: number;
}

export interface Document {
  id: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  category: string;
  /** The office's own label when the category is 'other'. */
  customCategory: string | null;
  jobId: string | null;
  userId: string | null;
  visibility: string;
  version: number;
  available: boolean;
  uploadedAt: string;
  uploadedByName: string | null;
}

export interface AuditEntry {
  id: number;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  before: unknown;
  after: unknown;
  ip_address: string | null;
  created_at: string;
  actor_name: string | null;
}

export interface Dashboard {
  clockedIn: {
    id: string;
    clock_in_at: string;
    clock_in_within_geofence: boolean | null;
    user_id: string;
    user_name: string;
    reference: string | null;
    title: string | null;
  }[];
  todayJobs: { status: string; count: number }[];
  exceptions: {
    id: string;
    clock_in_at: string;
    clock_out_at: string | null;
    status: string;
    exception_flags: string[];
    user_name: string;
    reference: string | null;
  }[];
  unassignedJobs: { id: string; reference: string; title: string; scheduled_start: string | null }[];
}

export interface Settings {
  defaultGeofenceRadiusM: number;
  accuracyTrustThresholdM: number;
  accuracyRejectThresholdM: number;
  maxShiftHours: number;
  maxOfflineQueueAgeHours: number;
  allowClockInOutsideGeofence: boolean;
  allowClockInBeforeStart: boolean;
  allowClockOutAfterEnd: boolean;
  workingHoursGraceMinutes: number;
  locationPingsEnabled: boolean;
  pingIntervalMinutes: number;
  timezone: string;
  /** The company as printed on employees' identification cards. */
  companyLegalName: string;
  companyVat: string;
  companyAddress: string;
  companyEmployer: string;
  /** Show the calendar in the mobile app. */
  calendarInApp: boolean;
  /** Employees may add appointments of their own. */
  calendarEmployeesCanAdd: boolean;
  /** Employees may request leave and permits from the app. */
  leaveEnabled: boolean;
  /** Managers may decide requests; when false only administrators do. */
  leaveManagersCanApprove: boolean;
  /** What can be requested. */
  leaveTypes: LeaveType[];
  /** Employees cannot open course and licence documents; the office still can. */
  hideCoursesFromEmployees: boolean;
}

/** A sign-in the office has just issued: the password is shown once and never stored. */
export interface IssuedLogin {
  loginName: string | null;
  temporaryPassword: string;
}

// --- Calendar and leave -------------------------------------------------------

export interface CalendarConfig {
  inApp: boolean;
  employeesCanAdd: boolean;
  canAdd: boolean;
  isOffice: boolean;
}

export type CalendarKind = 'appointment' | 'deadline' | 'leave';

export interface CalendarItem {
  /** Appointments carry a plain id; deadlines and leave are prefixed (`doc:`, `job:`, `leave:`...). */
  id: string;
  kind: CalendarKind;
  /** Deadlines: document, job, equipment or vehicle. Leave: approved or pending. */
  source: string;
  title: string;
  subtitle: string | null;
  /** An ISO timestamp, or a YYYY-MM-DD day for all-day things. */
  start: string;
  end: string | null;
  allDay: boolean;
  link: string | null;
  canEdit: boolean;
  /** Appointments only. */
  description?: string | null;
  place?: string | null;
  siteId?: string | null;
  jobId?: string | null;
  attendees?: { id: string; name: string }[];
}

export interface CalendarEventInput {
  title: string;
  description?: string | null;
  place?: string | null;
  startsAt: string;
  endsAt?: string | null;
  allDay: boolean;
  siteId?: string | null;
  jobId?: string | null;
  attendeeIds: string[];
}

export interface LeaveType {
  /** A lowercase slug, fixed when the type is created. */
  key: string;
  it: string;
  en: string;
  /** A permit in hours: one day, from a time to a time. */
  hours: boolean;
}

export interface LeaveConfig {
  enabled: boolean;
  types: LeaveType[];
  canDecide: boolean;
  isOffice: boolean;
}

export type LeaveStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface LeaveRequest {
  id: string;
  userId: string;
  person: string;
  kind: string;
  startsOn: string;
  endsOn: string;
  startTime: string | null;
  endTime: string | null;
  note: string | null;
  status: LeaveStatus;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
}


// --- Site assignments --------------------------------------------------------

export interface Assignment {
  id: string;
  userId: string;
  userName: string;
  siteId: string;
  siteName: string;
  city: string | null;
  geofenceRadiusM: number;
  jobId: string | null;
  jobReference: string | null;
  startsOn: string;
  /** null = open-ended, i.e. "on this project until told otherwise". */
  endsOn: string | null;
  notes: string | null;
  assignedByName: string | null;
  assignedAt: string;
}

// --- Document register -------------------------------------------------------

export type RegisterStatus = 'valid' | 'expiring' | 'expired' | 'missing' | 'not_provided' | 'exempt';

export interface RegisterFile {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  /** False for an entry recorded before its scan arrived: dates, no bytes. */
  hasFile: boolean;
  issuedAt: string | null;
  expiresAt: string | null;
  referenceNumber: string | null;
  notes: string | null;
  uploadedAt: string;
  uploadedByName: string | null;
  daysLeft: number | null;
  /** On a document's extra files (an ID card's back): the id of its first file. */
  bundleId: string | null;
}

/** One folder in a register. The flat fields describe its newest document. */
export interface RegisterItem {
  code: string;
  label: string;
  description: string | null;
  tracksExpiry: boolean;
  isMandatory: boolean;
  warnDaysBefore: number;
  parentCode: string | null;
  /** 0 for a top-level folder, 1 for a subfolder, and so on. */
  depth: number;
  /** Holds many documents (courses, nominations) rather than one current one. */
  allowsMultiple: boolean;
  /** Made by the office, so it can be renamed or removed. */
  isCustom: boolean;
  /** The code its files' automatic names start with ("C.I."); the label when null. */
  shortCode: string | null;
  /** This person (or vehicle) is excused from this required document. */
  exempt: boolean;
  files: RegisterFile[];
  documentId: string | null;
  filename: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  referenceNumber: string | null;
  uploadedAt: string | null;
  daysLeft: number | null;
  status: RegisterStatus;
}

export interface WorkerRegister {
  userId: string;
  userName: string;
  items: RegisterItem[];
  summary: { total: number; missing: number; expired: number; expiring: number };
}

export interface WorkerComplianceRow {
  id: string;
  name: string;
  role: string;
  requiredCount: number;
  presentCount: number;
  missingCount: number;
  expiredCount: number;
  expiringCount: number;
}

export interface DocumentType {
  code: string;
  scope: 'worker' | 'company' | 'job' | 'site';
  label_it: string;
  description_it: string | null;
  tracks_expiry: boolean;
  warn_days_before: number;
  is_mandatory: boolean;
  parent_code: string | null;
  allows_multiple: boolean;
  is_custom: boolean;
  short_code: string | null;
}

// --- Vehicles -------------------------------------------------------------------

export type VehicleStatus = 'available' | 'assigned' | 'maintenance' | 'retired';
export type VehicleFuel = 'petrol' | 'diesel' | 'electric' | 'hybrid' | 'lpg' | 'methane';

export interface Vehicle {
  id: string;
  plate: string;
  name: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  vin: string | null;
  fuel: VehicleFuel | null;
  registeredOn: string | null;
  odometerKm: number | null;
  status: VehicleStatus;
  notes: string | null;
  createdAt: string;
  holder: { userId: string; name: string | null; since: string | null; dueBackOn: string | null } | null;
  /** Expired, expiring and missing required documents: whether the van may be driven. */
  documents: { expired: number; expiring: number; missing: number; nextExpiry: string | null };
}

export interface VehicleAssignment {
  id: string;
  userId: string;
  userName: string | null;
  assignedAt: string;
  assignedByName: string | null;
  dueBackOn: string | null;
  odometerOut: number | null;
  notesOut: string | null;
  returnedAt: string | null;
  returnedToName: string | null;
  odometerIn: number | null;
  notesIn: string | null;
}

export interface VehicleInput {
  plate?: string;
  name?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  vin?: string | null;
  fuel?: VehicleFuel | null;
  registeredOn?: string | null;
  odometerKm?: number | null;
  notes?: string | null;
  status?: 'available' | 'maintenance' | 'retired';
}

/** What an employee's identification card still needs, and the photo it would use. */
export interface BadgeStatus {
  /** Fields still empty, by their Italian name ("Luogo di nascita"). The photo is separate. */
  missing: string[];
  /** The profile photo; `usable` false when it is a format the card cannot carry. */
  photo: { source: 'profile'; url: string; usable: boolean } | null;
  /** A picture among their documents named like a photo, when there is no usable one. */
  suggested: { source: 'document'; documentId: string; filename: string; url: string } | null;
}

/**
 * An image as a JPEG at most 900 px on its longer side: what a card needs and no more.
 * The original when the browser cannot read it (HEIC outside Safari), or it is not an image.
 */
export async function asJpeg(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 900 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.88));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

/** The lists that go to Excel and back. */
export type SheetKind = 'employees' | 'sites' | 'vehicles' | 'company';
const SHEET_PATHS: Record<SheetKind, string> = {
  employees: '/employees-sheet',
  sites: '/sites-sheet',
  vehicles: '/vehicles-sheet',
  company: '/company-sheet',
};

/** What an edited spreadsheet would change, or did. */
export interface SheetImport {
  applied: boolean;
  summary: { update: number; create: number; unchanged: number; error: number };
  rows: Array<{
    row: number;
    id: string | null;
    name: string;
    action: 'update' | 'create' | 'unchanged' | 'error';
    changes: Array<{ key: string; label: string; from: string | null; to: string | null }>;
    errors: string[];
    /** Worth knowing, not a problem: "new client". */
    notes?: string[];
  }>;
}

/** Whose documents, and which kinds, go into a pack for a customer. */
export interface PackSelection {
  userIds: string[];
  typeCodes: string[];
  companyTypeCodes: string[];
}

export interface PackPreview {
  files: Array<{
    id: string;
    userId: string | null;
    holder: string | null;
    typeCode: string;
    typeLabel: string;
    filename: string;
    sizeBytes: number;
    expiresAt: string | null;
  }>;
  missing: Array<{ userId: string; holder: string; typeCode: string; typeLabel: string }>;
  totalBytes: number;
}

export interface FolderInput {
  label: string;
  description?: string | null;
  allowsMultiple?: boolean;
  tracksExpiry?: boolean;
  warnDaysBefore?: number;
  isMandatory?: boolean;
  /** Starts its files' automatic names; null goes back to the label. */
  shortCode?: string | null;
}

// --- Alerts ------------------------------------------------------------------

export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface Alert {
  id: string;
  kind: string;
  severity: AlertSeverity;
  subjectType: string;
  subjectId: string;
  userId: string | null;
  userName: string | null;
  /**
   * The facts the alert is made of. The wording is rendered here, in the reader's
   * language — the backend deliberately does not store a finished sentence, because a
   * sentence has a language and the row would be stuck in whichever one the nightly job
   * happened to run in.
   */
  subjectLabel: string | null;
  holderName: string | null;
  /** Negative once past. Null when there is no date — a missing document. */
  daysLeft: number | null;
  /** Pre-rendered Italian, for rows written before the structured columns existed. */
  title: string | null;
  body: string | null;
  dueOn: string | null;
  createdAt: string;
  acknowledgedAt: string | null;
  acknowledgedByName: string | null;
  acknowledgedNote: string | null;
}

// --- Payroll -----------------------------------------------------------------

export interface PayrollRow {
  userId: string;
  userName: string;
  siteId: string | null;
  siteName: string | null;
  jobReference: string | null;
  date: string;
  clockIn: string;
  clockOut: string | null;
  breakMinutes: number;
  durationMinutes: number | null;
  withinGeofence: boolean | null;
  distanceM: number | null;
  status: string;
  flags: string[];
  overrideReason: string | null;
}

export interface PayrollGroup {
  label: string;
  shifts: number;
  minutes: number;
  excluded: number;
  rows: PayrollRow[];
}

export interface PayrollReport {
  period: { from: string; to: string; includeUnreviewed: boolean };
  groupBy: 'worker' | 'site';
  groups: PayrollGroup[];
  totals: { minutes: number; shifts: number; excluded: number };
}

export interface ShiftFilters {
  userId?: string;
  jobId?: string;
  from?: string;
  to?: string;
  status?: string;
  flagged?: boolean;
  /** Irregular and not yet signed off — the exception queue itself. */
  needsReview?: boolean;
  limit?: number;
  offset?: number;
}

function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

export const api = {
  me: () => get<{ user: User }>('/users/me'),

  dashboard: () => get<Dashboard>('/admin/dashboard'),

  users: (status?: string) => get<{ users: User[] }>(`/users${qs({ status })}`),
  user: (id: string) =>
    get<{ user: User; certifications: Record<string, unknown>[] }>(`/users/${id}`),
  createUser: (body: Record<string, unknown>) => post<{ user: User }>('/users', body),
  suspendUser: (id: string) => post<{ ok: true }>(`/users/${id}/suspend`),
  /** With `email` for someone who has none yet: the account is created with it. */
  setUserAccess: (id: string, access: { portalAccess: boolean; appAccess: boolean; email?: string }) =>
    patch<{ user: User }>(`/users/${id}/access`, access),
  reactivateUser: (id: string) => post<{ ok: true }>(`/users/${id}/reactivate`),
  updateUser: (id: string, details: PersonalDetails) => patch<{ user: User }>(`/users/${id}`, details),
  /**
   * The badge photo: a signed upload URL, the image straight to S3, then confirmed. Made
   * a JPEG of a sensible size first, so it can go on the printed card whatever the
   * phone or scanner produced.
   */
  async setUserPhoto(id: string, picked: File): Promise<string> {
    const file = await asJpeg(picked);
    const type = file.type || 'image/jpeg';
    const { key, uploadUrl } = await post<{ key: string; uploadUrl: string }>(`/users/${id}/photo-url`, {
      contentType: type,
    });
    const put = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'content-type': type } });
    if (!put.ok) throw new ApiError(put.status, 'UPLOAD_FAILED', 'Caricamento della foto non riuscito.');
    return (await post<{ ok: true; photoUrl: string }>(`/users/${id}/photo`, { key })).photoUrl;
  },
  removeUserPhoto: (id: string) => del<{ ok: true }>(`/users/${id}/photo`),

  /** The identification card: what it still needs, and which photo it would use. */
  badge: (userId: string) => get<BadgeStatus>(`/badges/${userId}`),
  /** Use a photo filed among the person's documents as their photo. */
  badgePhotoFromDocument: (userId: string, documentId: string) =>
    post<{ ok: true; photoUrl: string }>(`/badges/${userId}/photo-from-document`, { documentId }),
  downloadBadge: async (userId: string): Promise<{ blob: Blob; filename: string }> => {
    const response = await fetch(`${BASE_URL}/badges/${userId}/pdf`, { headers: await authHeaders() });
    if (!response.ok) {
      let message = `Tesserino non generato (errore ${response.status}).`;
      try {
        message = ((await response.json()) as { message?: string }).message ?? message;
      } catch {
        /* not JSON */
      }
      throw new ApiError(response.status, 'BADGE_FAILED', message);
    }
    const match = /filename="?([^";]+)"?/.exec(response.headers.get('content-disposition') ?? '');
    return { blob: await response.blob(), filename: match?.[1] ?? 'tesserino.pdf' };
  },
  deleteUser: (id: string) => del<{ ok: true }>(`/users/${id}`),
  /** A fresh temporary password by email, for someone who has not signed in yet. */
  reinviteUser: (id: string) =>
    post<{ ok: true; result: 'sent' | 'created' | 'not-configured'; email: string }>(`/users/${id}/reinvite`),

  /**
   * Sign in to the app with first name and surname, for someone with no portal access.
   * `loginName` only when the server could not derive one (422 LOGIN_NAME_REQUIRED).
   */
  createNameLogin: (id: string, loginName?: string) =>
    post<IssuedLogin & { loginName: string }>(`/users/${id}/name-login`, loginName ? { loginName } : {}),
  /** A new temporary password for anyone who already has a sign-in. */
  resetPassword: (id: string) => post<IssuedLogin>(`/users/${id}/reset-password`),

  messages: (archived: boolean) =>
    get<{ messages: ContactMessage[]; unread: number }>(`/messages${qs({ archived: archived || undefined })}`),
  readMessage: (id: string) => post<{ ok: true }>(`/messages/${id}/read`),
  archiveMessage: (id: string) => post<{ ok: true }>(`/messages/${id}/archive`),
  restoreMessage: (id: string) => post<{ ok: true }>(`/messages/${id}/restore`),

  notifications: (locale: string) =>
    get<{ notifications: AppNotification[]; unread: number }>(`/notifications${qs({ locale, limit: 50 })}`),
  readNotification: (id: string) => post<{ ok: true }>(`/notifications/${id}/read`),
  readAllNotifications: () => post<{ ok: true }>('/notifications/read-all'),
  notificationPreferences: () => get<NotificationPreferences>('/notifications/preferences'),
  saveNotificationPreferences: (body: {
    preferences?: Record<string, { enabled: boolean; channels: NotificationChannel[]; leadTimes: number[] }>;
    settings?: NotificationSettings;
  }) => put<{ ok: true }>('/notifications/preferences', body),
  webPushKey: () => get<{ publicKey: string | null }>('/notifications/web-push/key'),
  subscribeWebPush: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    post<{ ok: true }>('/notifications/web-push', subscription),
  unsubscribeWebPush: (endpoint: string) => post<{ ok: true }>('/notifications/web-push/unsubscribe', { endpoint }),

  /** What the signed-in person may open. Drives which nav items render. */
  myPermissions: () => get<PermissionsResponse>('/users/me/permissions'),
  userPermissions: (id: string) => get<PermissionsResponse>(`/users/${id}/permissions`),
  setUserPermissions: (id: string, permissions: PermissionGrid) =>
    put<{ ok: true; permissions: PermissionGrid }>(`/users/${id}/permissions`, { permissions }),

  shifts: (filters: ShiftFilters = {}) =>
    get<{ shifts: Shift[] }>(`/shifts${qs(filters as Record<string, unknown>)}`),
  shift: (id: string) =>
    get<{ shift: Shift; adjustments: ShiftAdjustment[] }>(`/shifts/${id}`),
  adjustShift: (id: string, body: { field: string; value: string; reason: string }) =>
    patch<{ shift: Shift }>(`/shifts/${id}`, body),
  /** "I looked at this and it is correct as recorded." Note optional. */
  reviewShift: (id: string, note?: string) =>
    post<{ shift: Shift }>(`/shifts/${id}/review`, { note }),
  /** Put it back in the queue — for when the wrong row was signed off. */
  unreviewShift: (id: string) => del<{ shift: Shift }>(`/shifts/${id}/review`),

  jobs: (status?: string) => get<{ jobs: Job[] }>(`/jobs${qs({ status })}`),
  job: (id: string) => get<{ job: Job }>(`/jobs/${id}`),
  createJob: (body: Record<string, unknown>) => post<{ job: Job }>('/jobs', body),
  deleteJob: (id: string) => del<{ ok: true }>(`/jobs/${id}`),
  updateJob: (id: string, body: Record<string, unknown>) =>
    patch<{ job: Job }>(`/jobs/${id}`, body),
  setJobStatus: (id: string, status: string, reason?: string) =>
    post<{ ok: true }>(`/jobs/${id}/status`, { status, reason }),
  assignJob: (id: string, userIds: string[]) =>
    post<{ ok: true }>(`/jobs/${id}/assign`, { userIds }),
  unassignJob: (id: string, userId: string) => del<{ ok: true }>(`/jobs/${id}/assign/${userId}`),
  jobNotes: (id: string) => get<{ notes: Record<string, unknown>[] }>(`/jobs/${id}/notes`),

  sites: () => get<{ sites: Site[] }>('/sites'),
  deleteSite: (id: string) => del<{ ok: true }>(`/sites/${id}`),
  createSite: (body: Record<string, unknown>) => post<{ site: Site }>('/sites', body),
  updateSite: (id: string, body: Record<string, unknown>) =>
    patch<{ site: Site }>(`/sites/${id}`, body),

  /**
   * Address autocomplete. Proxied through our API so no AWS credential reaches the
   * browser — see hh-backend/infra/lib/location-stack.ts. Returns an empty list rather
   * than failing when the place index is not configured, so the form stays usable.
   */
  suggestAddresses: (q: string, near?: LatLng) => {
    const params = new URLSearchParams({ q });
    if (near) {
      params.set('lat', String(near.lat));
      params.set('lng', String(near.lng));
    }
    return get<{ suggestions: AddressSuggestion[] }>(`/geo/suggest?${params}`);
  },

  /** What address is at this point? Used after dropping a pin on the map. */
  reverseGeocode: (at: LatLng) =>
    get<{ suggestion: AddressSuggestion | null }>(
      `/geo/reverse?lat=${at.lat}&lng=${at.lng}`,
    ),

  clients: () => get<{ clients: { id: string; name: string }[] }>('/clients'),
  /** Same name as an existing client returns that client rather than a duplicate. */
  createClient: (body: { name: string }) => post<{ client: { id: string; name: string } }>('/clients', body),

  /**
   * Upload a file. Three steps, because the bytes never pass through the API.
   *
   *   1. register the metadata and get a presigned PUT
   *   2. PUT the bytes straight to S3
   *   3. tell the API the bytes landed
   *
   * Step 2 going direct is the point: a 50 MB drawing through Lambda would be slow,
   * expensive and capped by the payload limit. Until step 3 runs the document is
   * invisible to every read, so a half-finished upload never looks available — and
   * that is exactly why this is one function rather than three the caller sequences
   * itself and can get wrong.
   */
  uploadDocument: async (
    file: File,
    meta: {
      category: DocumentCategory;
      jobId?: string;
      userId?: string;
      visibility?: DocumentVisibility;
      /** The office's own label. Only used when `category` is 'other'. */
      customCategory?: string;
      note?: string;
    },
  ): Promise<{ documentId: string }> => {
    const { documentId, uploadUrl } = await post<{ documentId: string; uploadUrl: string }>(
      '/documents/upload-url',
      {
        filename: file.name,
        // Browsers leave `type` empty for extensions they do not recognise; the server
        // has the allowlist and will say no properly, which beats guessing here.
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        ...meta,
      },
    );

    const put = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      // Must match the type the URL was signed for, or S3 rejects the signature.
      headers: { 'content-type': file.type || 'application/octet-stream' },
    });

    if (!put.ok) {
      throw new ApiError(put.status, 'UPLOAD_FAILED', 'Caricamento del file non riuscito.');
    }

    await post<{ ok: true }>(`/documents/${documentId}/complete`, {});
    return { documentId };
  },

  // --- Inventory --------------------------------------------------------------

  inventory: () => get<{ items: InventoryItem[] }>('/inventory'),
  inventoryItem: (id: string) =>
    get<{ item: InventoryItem; history: InventoryMovement[] }>(`/inventory/${id}`),
  inventoryCategories: () =>
    get<{ categories: InventoryCategory[] }>('/inventory/categories'),
  createInventoryItem: (body: Record<string, unknown>) =>
    post<{ item: InventoryItem }>('/inventory', body),
  updateInventoryItem: (id: string, body: Record<string, unknown>) =>
    patch<{ item: InventoryItem }>(`/inventory/${id}`, body),
  checkOutItem: (id: string, body: Record<string, unknown>) =>
    post<{ item: InventoryItem }>(`/inventory/${id}/check-out`, body),
  checkInItem: (id: string, body: Record<string, unknown>) =>
    post<{ item: InventoryItem }>(`/inventory/${id}/check-in`, body),
  deleteInventoryItem: (id: string) => del<{ ok: true }>(`/inventory/${id}`),
  /** Resolve a scanned label. Accepts our code, an external barcode, or a URL. */
  scanInventory: (code: string) =>
    get<{ item: InventoryItem }>(`/inventory/scan/${encodeURIComponent(code)}`),

  // --- Recycle bin -----------------------------------------------------------

  recycleBin: () => get<{ items: RecycleItem[]; retentionDays: number }>('/recycle-bin'),
  restoreItem: (kind: RecycleKind, id: string) =>
    post<{ ok: true }>(`/recycle-bin/${kind}/${id}/restore`, {}),
  /** Irreversible, and admin only. The server enforces both. */
  purgeItem: (kind: RecycleKind, id: string) =>
    del<{ ok: true }>(`/recycle-bin/${kind}/${id}`),

  documents: (filters: { jobId?: string; category?: string } = {}) =>
    get<{ documents: Document[] }>(`/documents${qs(filters)}`),
  downloadUrl: (id: string) => get<{ url: string }>(`/documents/${id}/download-url`),
  /** The same file, for showing in the portal's preview rather than saving. Audited as a view. */
  viewUrl: (id: string) => get<{ url: string }>(`/documents/${id}/download-url?inline=1`),
  deleteDocument: (id: string) => del<{ ok: true }>(`/documents/${id}`),
  /** Change who can see a document, or its own label. Office only, server-enforced. */
  updateDocument: (
    id: string,
    body: { visibility?: DocumentVisibility; customCategory?: string | null; filename?: string },
  ) =>
    patch<{ document: Document }>(`/documents/${id}`, body),

  audit: (filters: { action?: string; entityId?: string; limit?: number } = {}) =>
    get<{ entries: AuditEntry[] }>(`/admin/audit${qs(filters)}`),

  // --- Site assignments ------------------------------------------------------

  assignments: (filters: { from?: string; to?: string; userId?: string; siteId?: string } = {}) =>
    get<{ assignments: Assignment[] }>(`/assignments${qs(filters)}`),
  createAssignment: (body: {
    userIds: string[];
    siteId: string;
    jobId?: string;
    startsOn: string;
    endsOn?: string;
    notes?: string;
  }) => post<{ created: number; ids: string[] }>('/assignments', body),
  updateAssignment: (id: string, body: { startsOn?: string; endsOn?: string | null; notes?: string | null }) =>
    patch<{ assignment: Assignment }>(`/assignments/${id}`, body),
  removeAssignment: (id: string) => del<{ ok: true }>(`/assignments/${id}`),
  unassignedWorkers: (on: string) =>
    get<{ date: string; workers: { id: string; name: string; role: string }[] }>(
      `/assignments/unassigned${qs({ on })}`,
    ),

  // --- Document register -----------------------------------------------------

  documentTypes: (scope?: string) => get<{ types: DocumentType[] }>(`/register/types${qs({ scope })}`),

  // --- Vehicles -----------------------------------------------------------------
  vehicles: () => get<{ vehicles: Vehicle[] }>('/vehicles'),
  vehicle: (id: string) => get<{ vehicle: Vehicle; history: VehicleAssignment[] }>(`/vehicles/${id}`),
  vehiclesHeldBy: (userId: string) => get<{ vehicles: Vehicle[] }>(`/vehicles/held-by/${userId}`),
  createVehicle: (body: VehicleInput) => post<{ vehicle: Vehicle }>('/vehicles', body),
  updateVehicle: (id: string, body: VehicleInput) => patch<{ vehicle: Vehicle }>(`/vehicles/${id}`, body),
  assignVehicle: (id: string, body: { userId: string; dueBackOn?: string; odometerKm?: number; notes?: string }) =>
    post<{ vehicle: Vehicle }>(`/vehicles/${id}/assign`, body),
  returnVehicle: (id: string, body: { odometerKm?: number; notes?: string; status: 'available' | 'maintenance' }) =>
    post<{ vehicle: Vehicle }>(`/vehicles/${id}/return`, body),
  deleteVehicle: (id: string) => del<{ ok: true }>(`/vehicles/${id}`),
  vehicleRegister: (id: string) =>
    get<{ vehicleId: string; plate: string; items: RegisterItem[] }>(`/register/vehicles/${id}`),

  // --- Document packs: several people's documents, for a customer ---------------
  packStatus: () =>
    get<{ emailAvailable: boolean; maxEmailBytes: number; maxZipBytes: number }>('/document-packs/status'),
  packPreview: (sel: PackSelection) => post<PackPreview>('/document-packs/preview', sel),
  packZip: (sel: PackSelection) => post<{ url: string; files: number; sizeBytes: number }>('/document-packs/zip', sel),
  packEmail: (
    body: PackSelection & { to: string[]; copyToMe: boolean; subject: string; message: string },
  ) => post<{ sent: number; sizeBytes: number; missing: number }>('/document-packs/email', body),
  workerCompliance: () => get<{ workers: WorkerComplianceRow[] }>('/register/workers'),
  workerRegister: (userId: string) => get<WorkerRegister>(`/register/workers/${userId}`),
  companyRegister: () => get<{ items: RegisterItem[] }>('/register/company'),
  /** A new folder, in every employee's file (scope worker) or the company's. */
  createFolder: (body: FolderInput & { scope: 'worker' | 'company' | 'vehicle'; parentCode?: string }) =>
    post<{ type: DocumentType }>('/register/types', body),
  updateFolder: (code: string, body: Partial<FolderInput>) =>
    patch<{ type: DocumentType }>(`/register/types/${encodeURIComponent(code)}`, body),
  deleteFolder: (code: string) => del<{ ok: true }>(`/register/types/${encodeURIComponent(code)}`),
  /** Every file to the automatic names: apply false shows what would change. */
  renameAllFiles: (apply: boolean) =>
    post<{ applied: boolean; count: number; examples: Array<{ id: string; from: string; to: string }> }>(
      '/register/rename-all',
      { apply },
    ),
  /**
   * "Not required for this person" (or vehicle), and back. Only the office; every change
   * is in the activity log.
   */
  setExemption: (
    on: boolean,
    body: { typeCode: string; userId?: string; vehicleId?: string; reason?: string },
  ) =>
    on
      ? post<{ ok: true }>('/register/exemptions', body)
      : del<{ ok: true }>(
          `/register/exemptions${qs({ typeCode: body.typeCode, userId: body.userId, vehicleId: body.vehicleId })}`,
        ),
  /** Entries taken off a register, so they can be put back. */
  removedFolders: (scope: 'worker' | 'company' | 'vehicle') =>
    get<{ types: DocumentType[] }>(`/register/types/removed?scope=${scope}`),
  restoreFolder: (code: string) =>
    post<{ ok: true }>(`/register/types/${encodeURIComponent(code)}/restore`, {}),
  /** Correct a filed document's dates, number or notes. `null` clears a field. */
  updateDocumentDetails: (
    id: string,
    body: {
      issuedAt?: string | null;
      expiresAt?: string | null;
      referenceNumber?: string | null;
      notes?: string | null;
      /** A new name; the file's extension is kept by the server. */
      filename?: string;
    },
  ) => patch<{ ok: true }>(`/register/documents/${id}`, body),
  /**
   * Swap a filed document's file — the wrong scan, or a scan arriving after the dates
   * were recorded. Same three steps as an upload; the document keeps its place in the
   * history, and the old file stays until the new one is confirmed.
   */
  replaceDocumentFile: async (id: string, file: File): Promise<void> => {
    const type = file.type || 'application/octet-stream';
    const { key, uploadUrl } = await post<{ key: string; uploadUrl: string }>(
      `/register/documents/${id}/replace-url`,
      { filename: file.name, mimeType: type, sizeBytes: file.size },
    );
    const put = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'content-type': type } });
    if (!put.ok) throw new ApiError(put.status, 'UPLOAD_FAILED', 'Caricamento del file non riuscito.');
    await post<{ ok: true }>(`/register/documents/${id}/replace`, { key, filename: file.name, mimeType: type });
  },
  /**
   * Record a required document, with or without the file itself.
   *
   * Both are real. The office is often told a certificate's expiry date before the
   * scan of it arrives, and recording the date immediately is what makes the renewal
   * alert start counting down from the right day — so a file is optional. When one IS
   * supplied the server hands back a presigned PUT and the bytes go straight to S3,
   * exactly as in `uploadDocument`.
   */
  recordDocument: async (
    body: Record<string, unknown>,
    file?: File | null,
  ): Promise<{ documentId: string }> => {
    const { documentId, uploadUrl } = await post<{
      documentId: string;
      uploadUrl: string | null;
    }>('/register/documents', {
      ...body,
      ...(file
        ? {
            filename: file.name,
            mimeType: file.type || 'application/octet-stream',
            sizeBytes: file.size,
          }
        : {}),
    });

    if (!file || !uploadUrl) return { documentId };

    const put = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'content-type': file.type || 'application/octet-stream' },
    });

    if (!put.ok) {
      throw new ApiError(put.status, 'UPLOAD_FAILED', 'Caricamento del file non riuscito.');
    }

    await post<{ ok: true }>(`/documents/${documentId}/complete`, {});
    return { documentId };
  },

  // --- Alerts ----------------------------------------------------------------

  alerts: (includeAcknowledged = false) =>
    get<{ alerts: Alert[]; counts: Record<AlertSeverity, number> }>(
      `/register/alerts${qs({ includeAcknowledged })}`,
    ),
  acknowledgeAlert: (id: string, note: string) =>
    post<{ ok: true }>(`/register/alerts/${id}/acknowledge`, { note }),
  rescanAlerts: () => post<Record<string, number>>('/register/alerts/rescan'),

  // --- Payroll ---------------------------------------------------------------

  payroll: (params: {
    from: string;
    to: string;
    groupBy: 'worker' | 'site';
    userId?: string;
    siteId?: string;
    includeUnreviewed?: boolean;
  }) => get<PayrollReport>(`/payroll${qs({ ...params, format: 'json' })}`),

  /**
   * Download a binary export.
   *
   * Fetched rather than linked to. A plain `window.location.href` would carry no auth
   * header at all — no `x-dev-user` now, no bearer token after Cognito — so the browser
   * would land on a 401 page instead of a file. Exports are kilobytes, not megabytes,
   * so buffering one is cheap; correctness is not.
   */
  downloadPayroll: async (params: {
    from: string;
    to: string;
    groupBy: 'worker' | 'site';
    format: 'xlsx' | 'pdf';
    includeUnreviewed?: boolean;
  }): Promise<{ blob: Blob; filename: string }> => {
    const response = await fetch(`${BASE_URL}/payroll${qs(params)}`, {
      headers: await authHeaders(),
    });

    if (!response.ok) {
      throw new ApiError(
        response.status,
        'EXPORT_FAILED',
        `Esportazione non riuscita (errore ${response.status}).`,
      );
    }

    // The server already names the file; fall back only if the header is missing.
    const disposition = response.headers.get('content-disposition') ?? '';
    const match = /filename="?([^";]+)"?/.exec(disposition);
    const filename = match?.[1] ?? `ore_${params.from}_${params.to}.${params.format}`;

    return { blob: await response.blob(), filename };
  },

  /** Everybody's details as an Excel file, to edit and import back. */
  downloadSheet: async (kind: SheetKind): Promise<{ blob: Blob; filename: string }> => {
    const response = await fetch(`${BASE_URL}${SHEET_PATHS[kind]}`, { headers: await authHeaders() });
    if (!response.ok) {
      throw new ApiError(response.status, 'EXPORT_FAILED', `Esportazione non riuscita (errore ${response.status}).`);
    }
    const disposition = response.headers.get('content-disposition') ?? '';
    const match = /filename="?([^";]+)"?/.exec(disposition);
    return { blob: await response.blob(), filename: match?.[1] ?? `${kind}.xlsx` };
  },
  /** Check an edited sheet (apply false), or apply it. */
  importSheet: (kind: SheetKind, file: string, apply: boolean) =>
    post<SheetImport>(`${SHEET_PATHS[kind]}/import`, { file, apply }),

  calendarConfig: () => get<CalendarConfig>('/calendar/config'),
  /** Everything on the calendar between two days (YYYY-MM-DD, at most 93 apart). */
  calendar: (from: string, to: string) => get<{ items: CalendarItem[] }>(`/calendar${qs({ from, to })}`),
  createCalendarEvent: (body: CalendarEventInput) => post<{ id: string }>('/calendar/events', body),
  updateCalendarEvent: (id: string, body: CalendarEventInput) =>
    patch<{ id: string }>(`/calendar/events/${id}`, body),
  deleteCalendarEvent: (id: string) => del<{ ok: true }>(`/calendar/events/${id}`),

  leaveConfig: () => get<LeaveConfig>('/leave/config'),
  leave: (status: LeaveStatus | 'all') => get<{ requests: LeaveRequest[] }>(`/leave${qs({ status })}`),
  decideLeave: (id: string, decision: 'approved' | 'rejected', note?: string) =>
    post<{ ok: true }>(`/leave/${id}/decide`, { decision, note: note || null }),

  settings: () => get<{ settings: Settings }>('/admin/settings'),
  updateSettings: (body: Record<string, unknown>) =>
    patch<{ settings: Settings }>('/admin/settings', body),
};
